import React, { createContext, useState, useCallback, useContext, useMemo, useEffect } from 'react'
import { Extension } from '@codemirror/state'
import { EditorContent, EditorTab, ValidationError } from './types'

// Cross-domain communication interfaces
export interface EditorConfiguration {
	wysiwyg: {
		rows: number
		placeholder: string
		style: React.CSSProperties
		minHeight: number
		theme: 'light'
	}
	html: {
		minHeight: number
		theme: 'dark'
		extensions: Extension[] // CodeMirror extensions
	}
	css: {
		minHeight: number
		theme: 'dark'
		extensions: Extension[] // CodeMirror extensions
	}
}

export interface EditorContextValue {
	// Content State
	content: EditorContent
	activeTab: EditorTab
	isDirty: boolean
	errors: ValidationError[]

	// Specialized Content Operations (stable callbacks)
	updateWysiwyg: (value: string) => void
	updateHtml: (value: string) => void
	updateCss: (value: string) => void
	updateContent: (field: keyof EditorContent, value: string) => void // Generic updater
	setContent: (content: EditorContent) => void // Bulk content update

	// Tab Management
	switchTab: (tab: EditorTab) => void
	getTabContent: (tab: EditorTab) => string

	// State Management
	markDirty: () => void
	markClean: () => void
	resetContent: () => void

	// Validation & Errors
	validateContent: () => ValidationError[]
	clearErrors: () => void

	// Configuration (stable references)
	editorConfig: EditorConfiguration

	// Cross-Domain Communication Events
	onContentUpdate?: (content: EditorContent) => void // Notify other domains
	onDirtyStateChange?: (isDirty: boolean) => void // Notify state changes
}

export interface EditorProviderProps {
	children: React.ReactNode
	// Initial state
	initialContent?: EditorContent
	initialTab?: EditorTab
	// External content synchronization (for template integration)
	externalContent?: EditorContent
	// Cross-domain event handlers (injected from parent)
	onContentUpdate?: (content: EditorContent) => void
	onDirtyStateChange?: (isDirty: boolean) => void
}

const EditorContext = createContext<EditorContextValue | null>(null)

/**
 * EditorProvider: Clean editor state management with cross-domain communication
 *
 * This provider eliminates prop drilling by managing all editor-related state
 * and provides event-based communication for cross-domain operations.
 *
 * Cross-domain operations (editor ↔ template) are handled through
 * injected callback functions rather than direct state coupling.
 */
export const EditorProvider: React.FC<EditorProviderProps> = ({
	children,
	initialContent = { wysiwyg: '', html: '', css: '' },
	initialTab = 'content',
	externalContent,
	onContentUpdate,
	onDirtyStateChange,
}) => {
	// Editor state - isolated to this domain
	const [content, setContent] = useState<EditorContent>(initialContent)
	const [activeTab, setActiveTab] = useState<EditorTab>(initialTab)
	const [isDirty, setIsDirty] = useState(false)
	const [errors, setErrors] = useState<ValidationError[]>([])

	// 🎯 SYNC: External content changes (e.g., from template selection)
	useEffect(() => {
		if (
			externalContent &&
			(externalContent.wysiwyg !== content.wysiwyg ||
				externalContent.html !== content.html ||
				externalContent.css !== content.css)
		) {
			setContent(externalContent)
			onContentUpdate?.(externalContent)
		}
	}, [externalContent, content, onContentUpdate])

	// 🎯 PERFORMANCE: Centralized, memoized configuration
	const editorConfig = useMemo<EditorConfiguration>(() => {
		const baseHeight = 300 // 15 rows * 20px

		return {
			wysiwyg: {
				rows: 15,
				placeholder: 'Start writing your article content here...',
				minHeight: baseHeight,
				theme: 'light' as const,
				style: {
					fontFamily: 'Georgia, "Times New Roman", serif',
					fontSize: '16px',
					lineHeight: '1.6',
					width: '100%',
					height: '100%',
					border: 'none',
					outline: 'none',
					backgroundColor: 'transparent',
					color: '#2c3e50',
					resize: 'none' as const,
					padding: '0',
					boxShadow: 'none',
					WebkitBoxShadow: 'none',
					MozBoxShadow: 'none',
				},
			},
			html: {
				minHeight: baseHeight,
				theme: 'dark' as const,
				extensions: [], // Will be populated by components
			},
			css: {
				minHeight: baseHeight,
				theme: 'dark' as const,
				extensions: [], // Will be populated by components
			},
		}
	}, [])

	// 🎯 PERFORMANCE: Specialized, stable content updaters
	const updateWysiwyg = useCallback(
		(value: string) => {
			setContent((prev) => {
				const newContent = { ...prev, wysiwyg: value }
				onContentUpdate?.(newContent)
				return newContent
			})
			setIsDirty(true)
			onDirtyStateChange?.(true)
		},
		[onContentUpdate, onDirtyStateChange],
	)

	const updateHtml = useCallback(
		(value: string) => {
			setContent((prev) => {
				const newContent = { ...prev, html: value }
				onContentUpdate?.(newContent)
				return newContent
			})
			setIsDirty(true)
			onDirtyStateChange?.(true)
		},
		[onContentUpdate, onDirtyStateChange],
	)

	const updateCss = useCallback(
		(value: string) => {
			setContent((prev) => {
				const newContent = { ...prev, css: value }
				onContentUpdate?.(newContent)
				return newContent
			})
			setIsDirty(true)
			onDirtyStateChange?.(true)
		},
		[onContentUpdate, onDirtyStateChange],
	)

	// Generic content updater for backward compatibility
	const updateContent = useCallback(
		(field: keyof EditorContent, value: string) => {
			setContent((prev) => {
				const newContent = { ...prev, [field]: value }
				onContentUpdate?.(newContent)
				return newContent
			})
			setIsDirty(true)
			onDirtyStateChange?.(true)
		},
		[onContentUpdate, onDirtyStateChange],
	)

	// Bulk content update (for template integration)
	const setContentBulk = useCallback(
		(newContent: EditorContent) => {
			setContent(newContent)
			onContentUpdate?.(newContent)
			setIsDirty(true)
			onDirtyStateChange?.(true)
		},
		[onContentUpdate, onDirtyStateChange],
	)

	// 🎯 PERFORMANCE: Stable tab switching
	const switchTab = useCallback((tab: EditorTab) => {
		setActiveTab(tab)
	}, [])

	// Get content for specific tab
	const getTabContent = useCallback(
		(tab: EditorTab): string => {
			switch (tab) {
				case 'content':
					return content.wysiwyg
				case 'html':
					return content.html
				case 'css':
					return content.css
				default:
					return content.wysiwyg
			}
		},
		[content],
	)

	// State management operations
	const markDirty = useCallback(() => {
		setIsDirty(true)
		onDirtyStateChange?.(true)
	}, [onDirtyStateChange])

	const markClean = useCallback(() => {
		setIsDirty(false)
		onDirtyStateChange?.(false)
	}, [onDirtyStateChange])

	const resetContent = useCallback(() => {
		setContent(initialContent)
		setActiveTab(initialTab)
		setIsDirty(false)
		setErrors([])
		onContentUpdate?.(initialContent)
		onDirtyStateChange?.(false)
	}, [initialContent, initialTab, onContentUpdate, onDirtyStateChange])

	// Content validation
	const validateContent = useCallback((): ValidationError[] => {
		const validationErrors: ValidationError[] = []

		// Check if content is empty
		if (!content.wysiwyg.trim() && !content.html.trim()) {
			validationErrors.push({
				field: 'content',
				message: 'Content cannot be empty',
				type: 'error',
			})
		}

		// Check content length (example validation)
		if (content.wysiwyg.length > 10000) {
			validationErrors.push({
				field: 'wysiwyg',
				message: 'Content exceeds maximum length of 10,000 characters',
				type: 'warning',
			})
		}

		// Validate HTML syntax (basic check)
		if (content.html.trim() && !content.html.includes('<') && !content.html.includes('>')) {
			validationErrors.push({
				field: 'html',
				message: 'HTML content should contain valid HTML tags',
				type: 'warning',
			})
		}

		setErrors(validationErrors)
		return validationErrors
	}, [content])

	const clearErrors = useCallback(() => {
		setErrors([])
	}, [])

	// 🎯 MEMORY: Stable context value
	const contextValue = useMemo(
		() => ({
			// State
			content,
			activeTab,
			isDirty,
			errors,

			// Content operations
			updateWysiwyg,
			updateHtml,
			updateCss,
			updateContent,
			setContent: setContentBulk,

			// Tab management
			switchTab,
			getTabContent,

			// State management
			markDirty,
			markClean,
			resetContent,

			// Validation
			validateContent,
			clearErrors,

			// Configuration
			editorConfig,

			// Cross-domain communication (pass through for child hooks)
			onContentUpdate,
			onDirtyStateChange,
		}),
		[
			content,
			activeTab,
			isDirty,
			errors,
			updateWysiwyg,
			updateHtml,
			updateCss,
			updateContent,
			setContentBulk,
			switchTab,
			getTabContent,
			markDirty,
			markClean,
			resetContent,
			validateContent,
			clearErrors,
			editorConfig,
			onContentUpdate,
			onDirtyStateChange,
		],
	)

	return <EditorContext.Provider value={contextValue}>{children}</EditorContext.Provider>
}

/**
 * Base hook to access editor context
 * Throws error if used outside EditorProvider
 */
export const useEditorContext = (): EditorContextValue => {
	const context = useContext(EditorContext)
	if (!context) {
		throw new Error('useEditorContext must be used within an EditorProvider')
	}
	return context
}
